import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";

import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getSessionFilters } from "../../utils/FilterHelpers";
import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import { getObject, setObject } from "../../utils/StorageHelpers";
import { longDateTime } from "../../utils/DateHelper";
import LookupsAPI from "../../API/lookups.api";
import { mapLookupToSelect } from "../../utils/Helpers";
import {
    getCountriesDropdown,
    getCountryWithFlag,
} from "../../utils/CountryHelper";
import SuppliersModal from "../../components/Modals/SuppliersModal";
import SuppliersAPI from "../../API/suppliers.api";
import ProductTags from "../../components/Tags/ProductTags";
import { SuccessAlert } from "../../utils/Alerts";
import MissingData from "../../components/Shared/MissingData";
import { FIELD_TYPES } from "../../utils/constants";

const MaterialSuppliers = () => {
    //General States
    const [isLoading, setIsLoading] = useState(false);

    //Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Materials-Suppliers-itemsPerPage", true) || 10
    );
    const [suppliersTotalCount, setSuppliersTotalCount] = useState(0);

    //Data States
    const [suppliers, setSuppliers] = useState([]);

    //Modal States
    const [isSupplierModalVisible, setIsSupplierModalVisible] = useState(false);
    const [isSupplierModalSubmitting, setIsSupplierModalSubmitting] =
        useState(false);
    const [selectedSupplier, setSelectedSupplier] = useState({});
    const [error, setError] = useState({});

    //Filter States
    const [filters, setFilters] = useState({});
    const [filtersUsed, setFiltersUsed] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("materials-suppliers")
    );
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);

    //Filter lookup states
    const [materialsLookup, setMaterialsLookup] = useState([]);
    const [countriesLookup, setCountriesLookup] = useState([]);

    //Constants
    const filterOptions = [
        {
            label: t("name"),
            name: "name",
            type: FIELD_TYPES.Input,
            placeholder: t("inputPlaceholder", { entity: t("name") }),
        },
        {
            label: t("codeName"),
            name: "codeName",
            type: FIELD_TYPES.Input,
            placeholder: t("inputPlaceholder", { entity: t("codeName") }),
        },
        {
            label: t("country"),
            name: "supportedCountryId",
            type: FIELD_TYPES.Dropdown,
            options: countriesLookup,
        },
        {
            label: t("material"),
            name: "materialId",
            type: FIELD_TYPES.Dropdown,
            options: materialsLookup,
        },
    ];

    const keys = [
        { name: "name" },
        { name: "codeName" },
        {
            name: "materials",
            element: (row) => (
                <ProductTags
                    center={true}
                    tags={row.materials}
                    emptyMessage="---"
                    keyName="label"
                />
            ),
        },
        {
            name: "supportedCountries",
            element: (row) => (
                <ProductTags
                    center={true}
                    tags={[getCountryWithFlag(row.supportedCountry)]}
                    emptyMessage="---"
                    keyName="label"
                />
            ),
        },
        { name: "description" },
        { name: "createdAt", parseMethod: longDateTime },
    ];

    const headers = [
        { label: t("name"), name: "name" },
        { label: t("codeName"), name: "codeName", skipSort: true },
        { label: t("materials"), name: "materials", skipSort: true },
        { label: t("country"), name: "supportedCountries", skipSort: true },
        { label: t("description"), name: "description", skipSort: true },
        { label: t("createdAt"), name: "createdAt" },
        { label: t("actions"), skipSort: true },
    ];

    const supplierLocalization = { entity: t("materialSupplier") };

    useEffect(() => {
        getSuppliers();
        getCountriesLookup();
        getMaterialsLookup();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        getSuppliers(filters, sortBy, sortDirection, 0);
    }, [sortBy, sortDirection]);

    //API functions
    const getSuppliers = async (
        filters = null,
        sortBy = null,
        sortDirection = null,
        offset = 0,
        itemsPerPage = itemsPerRequest || 10
    ) => {
        setIsLoading(true);
        const result = await SuppliersAPI.getSuppliers(
            itemsPerPage,
            filters,
            offset,
            sortBy,
            sortDirection
        );
        setSuppliersTotalCount(result?.totalCount);
        setSuppliers(result?.data);
        setIsLoading(false);
    };

    const getCountriesLookup = async () => {
        getCountriesDropdown(setCountriesLookup, true);
    };

    const getMaterialsLookup = async () => {
        const result = await LookupsAPI.getMaterials();
        setMaterialsLookup(mapLookupToSelect(result));
    };

    const deleteSupplier = async (supplierIndex) => {
        const result = await SuppliersAPI.deleteSupplier(
            suppliers[supplierIndex].id
        );
        if (result?.deleted) {
            removeSupplierFromState(supplierIndex);
            SuccessAlert(t("deleteSuccessMessage", supplierLocalization));
        }
    };

    const updateSupplier = async (updatedSupplier) => {
        setIsSupplierModalSubmitting(true);
        const result = await SuppliersAPI.updateSupplier(updatedSupplier);
        if (result?.modified) {
            setIsSupplierModalVisible(false);
            SuccessAlert(t("updateSuccessMessage", supplierLocalization));
            getSuppliers(filters, sortBy, sortDirection, 0);
        } else if (!result?.inserted && result?.errors) {
            setError(result?.errors);
        }
        setIsSupplierModalSubmitting(false);
    };

    const createSupplier = async (createdSupplier) => {
        setIsSupplierModalSubmitting(true);
        const result = await SuppliersAPI.createSupplier(createdSupplier);
        if (result?.inserted) {
            setIsSupplierModalVisible(false);
            SuccessAlert(t("addSuccessMessage", supplierLocalization));
            getSuppliers(filters, sortBy, sortDirection, 0);
        } else if (!result?.inserted && result?.errors) {
            setError(result?.errors);
        }
        setIsSupplierModalSubmitting(false);
    };

    //Modal functions
    const handleEditSelected = (index) => {
        setSelectedSupplier(suppliers[index]);
        setIsSupplierModalVisible(true);
    };

    //Filter functions
    const applyFilters = (specificAppliedFilters) => {
        getSuppliers(specificAppliedFilters, sortBy, sortDirection);
        setFiltersUsed(true);
    };

    //Pagination functions
    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            getSuppliers(filters, sortBy, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Materials-Suppliers-itemsPerPage", itemsPerPage, true);
        getSuppliers(filters, sortBy, sortDirection, 0, itemsPerPage);
    };

    //Functionalities
    const removeSupplierFromState = (index) => {
        let newSuppliers = [...suppliers];
        newSuppliers.splice(index, 1);
        setSuppliers(newSuppliers);
        if (!newSuppliers.length) setSuppliersTotalCount(0);
    };

    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1">
                    <Trans i18nKey="materialSuppliers">
                        Materials Suppliers
                    </Trans>
                </h1>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={filterOptions}
                        filters={filters}
                        setFilters={setFilters}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        component="materials-suppliers"
                        applyFilters={applyFilters}
                    />
                    <button
                        className="default-orange-outline-btn ml-2"
                        onClick={() => {
                            setIsSupplierModalVisible(true);
                        }}
                    >
                        <i className="fas fa-plus mr-2" />
                        <Trans i18nKey="newSupplier">New Supplier</Trans>
                    </button>
                </div>
            </header>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    {suppliersTotalCount > 0 ? (
                        <>
                            <CardTable
                                keys={keys}
                                headers={headers}
                                rows={suppliers}
                                purpose={"Suppliers"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                actionColumn={true}
                                hasDeleteAccess={true}
                                handleDelete={deleteSupplier}
                                handleEditClicked={handleEditSelected}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={suppliersTotalCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("materialSuppliers")}
                            includeText={!isLoading}
                            clickCallback={() =>
                                setIsSupplierModalVisible(true)
                            }
                        />
                    )}
                </div>
            </LoadingOverlay>
            <SuppliersModal
                isVisible={isSupplierModalVisible}
                setIsVisible={setIsSupplierModalVisible}
                isModalSubmitting={isSupplierModalSubmitting}
                editAction={updateSupplier}
                addAction={createSupplier}
                data={selectedSupplier}
                setData={setSelectedSupplier}
                countriesLookup={countriesLookup}
                materialsLookup={materialsLookup}
                error={error}
                setError={setError}
            />
        </>
    );
};

export default MaterialSuppliers;
